/**
 * Tournament simulation and winner selection logic
 */

import type { TreeNode } from './types';
import { getNodesAtLevel } from './treeGenerator';

/**
 * Selects winners for the entire tournament tree
 * Returns a map of element IDs to their winner status
 */
export function selectWinners(
  tree: TreeNode,
  depth: number,
): Map<string, boolean> {
  const winners = new Map<string, boolean>();
  
  // Handle victory node structure - the actual tournament root is the first child
  const tournamentRoot = tree.id === 'node-victory' && tree.children?.[0] ? tree.children[0] : tree;
  
  // Start with all leaf nodes as potential winners
  const leafNodes = getNodesAtLevel(tournamentRoot, depth - 1);
  leafNodes.forEach(node => winners.set(node.id, true));
  
  // Process from bottom to top
  for (let level = depth - 2; level >= 0; level--) {
    processLevel(tournamentRoot, level, winners);
  }
  
  // Ensure root node is marked as a winner if it has winning children
  if (!winners.has(tournamentRoot.id)) {
    winners.set(tournamentRoot.id, true);
  }
  
  // Mark victory node as winner if we have one
  if (tree.id === 'node-victory') {
    winners.set('node-victory', true);
  }
  
  // Mark paths based on node winner status
  markPaths(tree, winners);
  
  return winners;
}

/**
 * Process a single level of the tournament
 */
function processLevel(
  tree: TreeNode,
  level: number,
  winners: Map<string, boolean>,
): void {
  const nodesAtLevel = getNodesAtLevel(tree, level);
  
  nodesAtLevel.forEach(node => {
    if (node.children && node.children.length === 2) {
      const [left, right] = node.children;
      
      const leftWins = winners.get(left.id) ?? false;
      const rightWins = winners.get(right.id) ?? false;
      
      if (leftWins && rightWins) {
        // Both children are winners, pick one randomly
        const winner = selectRandomWinner(left, right);
        const loser = winner === left ? right : left;
        winners.set(loser.id, false);
        winners.set(node.id, true); // Parent becomes a winner
      } else if (leftWins || rightWins) {
        // Only one child is a winner, parent inherits winner status
        winners.set(node.id, true);
      } else {
        // Neither child is a winner
        winners.set(node.id, false);
      }
    }
  });
}

/**
 * Randomly selects a winner between two nodes
 */
function selectRandomWinner(node1: TreeNode, node2: TreeNode): TreeNode {
  return Math.random() > 0.5 ? node1 : node2;
}

/**
 * Marks paths as winners or losers based on connected nodes
 */
function markPaths(node: TreeNode, winners: Map<string, boolean>): void {
  if (node.children) {
    node.children.forEach(child => {
      const pathId = `path-${node.id}-${child.id}`;
      const childWins = winners.get(child.id) ?? false;
      // A path is a winner if the child node is a winner (showing their winning path up to where they lost)
      winners.set(pathId, childWins);
      markPaths(child, winners);
    });
  }
}

/**
 * Identifies the ultimate winning path - paths that lead to the victory node
 */
export function getUltimateWinningPath(
  tree: TreeNode,
  winners: Map<string, boolean>,
): Set<string> {
  const ultimateWinners = new Set<string>();
  
  // Start from victory node and trace back through winners
  function traceWinningPath(node: TreeNode): void {
    // Add this node to ultimate winners if it's a winner
    if (winners.get(node.id)) {
      ultimateWinners.add(node.id);
      
      // Add path from parent to this node if parent exists
      if (node.parent) {
        const pathId = `path-${node.parent.id}-${node.id}`;
        if (winners.get(pathId)) {
          ultimateWinners.add(pathId);
        }
      }
      
      // Recursively trace through winning children
      if (node.children) {
        node.children.forEach(child => {
          if (winners.get(child.id)) {
            traceWinningPath(child);
          }
        });
      }
    }
  }
  
  // Start tracing from the victory node if it exists, otherwise from root
  if (tree.id === 'node-victory') {
    traceWinningPath(tree);
  } else {
    traceWinningPath(tree);
  }
  
  return ultimateWinners;
}

/**
 * Creates a deterministic winner selection for testing
 */
export function selectWinnersFixed(
  tree: TreeNode,
  depth: number,
  seed: number = 0,
): Map<string, boolean> {
  const winners = new Map<string, boolean>();
  let rngState = seed;
  
  // Simple LCG for deterministic randomness
  const random = (): number => {
    rngState = (rngState * 1103515245 + 12345) % 2147483648;
    return rngState / 2147483648;
  };
  
  // Start with all leaf nodes as potential winners
  const leafNodes = getNodesAtLevel(tree, depth - 1);
  leafNodes.forEach(node => winners.set(node.id, true));
  
  // Process from bottom to top
  for (let level = depth - 2; level >= 0; level--) {
    const nodesAtLevel = getNodesAtLevel(tree, level);
    
    nodesAtLevel.forEach(node => {
      if (node.children && node.children.length === 2) {
        const [left, right] = node.children;
        
        const leftWins = winners.get(left.id) ?? false;
        const rightWins = winners.get(right.id) ?? false;
        
        if (leftWins && rightWins) {
          // Use deterministic random
          const winner = random() > 0.5 ? left : right;
          const loser = winner === left ? right : left;
          winners.set(loser.id, false);
          winners.set(node.id, true);
        } else if (leftWins || rightWins) {
          winners.set(node.id, true);
        } else {
          winners.set(node.id, false);
        }
      }
    });
  }
  
  markPaths(tree, winners);
  return winners;
}